import { useEffect, useState } from 'react';
import { screenSizes } from '@bridebook/ui';
import { useScreenWidth } from './use-screen-width';

/**
 * A hook that implements a mobile-first responsive design approach by checking if the screen width
 * matches or exceeds a given breakpoint.
 *
 * The mobile-first approach means we start with styles for smallest screens and progressively enhance
 * for larger screens. This is reflected in the logic where we check if width is >= breakpoint,
 * rather than checking if it's smaller. This matches how CSS min-width media queries work.
 *
 * For example:
 * - Mobile styles are the default (no breakpoint check needed)
 * - Tablet styles activate when width >= tablet breakpoint
 * - Desktop styles activate when width >= desktop breakpoint
 *
 * This creates a natural cascade from simple mobile layouts to more complex desktop layouts.
 *
 * @param size - Either a key from screenSizes or a number representing the breakpoint in pixels
 * @returns boolean - True if the current screen width matches or exceeds the breakpoint
 */
export function useBreakpoint(size: keyof typeof screenSizes | number) {
  const [matches, setMatches] = useState(false);
  const screenWidth = useScreenWidth();

  useEffect(() => {
    setMatches(screenWidth >= screenSizes[size]);
  }, [size, screenWidth]);

  return matches;
}
